import React, { useMemo, useState } from 'react';
import { RentPin } from '../types';
import './RentalListingsSidebar.css';

const FURNISH_OPTS = ['Furnished', 'Semi-Furnished', 'Unfurnished'];
const PROP_TYPES = ['Basement', 'Apartment', 'Condo', 'Townhouse', 'Semi-Detached', 'Detached'];

interface RentalListingsSidebarProps {
  rentals: RentPin[];
  selectedRental: RentPin | null;
  onSelectRental: (rental: RentPin) => void;
  onViewDetails?: (rental: RentPin) => void;
  totalCount: number;
  loading: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  priceFilter: { min: number; max: number };
  onPriceFilterChange: (min: number, max: number) => void;
  bedroomFilter: string | null;
  onBedroomFilterChange: (bedrooms: string | null) => void;
  furnishFilter: string | null;
  onFurnishFilterChange: (f: string | null) => void;
  propTypeFilter: string[];
  onPropTypeFilterChange: (t: string[]) => void;
  onClose: () => void;
}

export const RentalListingsSidebar: React.FC<RentalListingsSidebarProps> = ({
  rentals,
  selectedRental,
  onSelectRental,
  onViewDetails,
  totalCount,
  loading,
  hasMore,
  onLoadMore,
  priceFilter,
  onPriceFilterChange,
  bedroomFilter,
  onBedroomFilterChange,
  furnishFilter,
  onFurnishFilterChange,
  propTypeFilter,
  onPropTypeFilterChange,
  onClose,
}) => {
  const [showFilters, setShowFilters] = useState(false);

  const filteredRentals = useMemo(() => rentals.filter(rent => {
    if (rent.monthly_rent < priceFilter.min || rent.monthly_rent > priceFilter.max) return false;
    if (bedroomFilter !== null) {
      const n = parseInt(bedroomFilter);
      if (bedroomFilter.endsWith('+') ? rent.bedrooms < n : rent.bedrooms !== n) return false;
    }
    if (furnishFilter && rent.furnishing !== furnishFilter) return false;
    if (propTypeFilter.length > 0 && !propTypeFilter.includes(rent.property_type ?? '')) return false;
    return true;
  }), [rentals, priceFilter, bedroomFilter, furnishFilter, propTypeFilter]);

  const activeFilterCount = [
    bedroomFilter !== null,
    furnishFilter !== null,
    propTypeFilter.length > 0,
    priceFilter.max < 9999,
  ].filter(Boolean).length;

  return (
    <div className="rentals-sidebar">
      <div className="sidebar-header">
        <div>
          <h2>GTA Rentals</h2>
          <span className="sidebar-sub">{filteredRentals.length} of {totalCount} pins</span>
        </div>
        <button className="sidebar-close" onClick={onClose} title="Close">✕</button>
      </div>

      {/* ── Filters ── */}
      <div className="filter-section">
        <button className="filter-toggle" onClick={() => setShowFilters(s => !s)}>
          Filters {activeFilterCount > 0 && <span className="filter-badge">{activeFilterCount}</span>}
          <span className="filter-chevron">{showFilters ? '▲' : '▼'}</span>
        </button>

        {showFilters && (
          <div className="filters">
            {/* Price */}
            <div className="filter-group">
              <label>Price: ${priceFilter.min.toLocaleString()} – ${priceFilter.max.toLocaleString()}</label>
              <div className="price-range-row">
                <input type="range" min="0" max="9999" step="100"
                  value={priceFilter.max} className="price-slider"
                  onChange={e => onPriceFilterChange(priceFilter.min, Number(e.target.value))} />
              </div>
            </div>

            {/* Bedrooms */}
            <div className="filter-group">
              <label>Bedrooms</label>
              <div className="pill-row">
                {(['1', '1+', '2', '2+', '3+'] as const).map(b => (
                  <button key={b}
                    className={`filter-pill${bedroomFilter === b ? ' active' : ''}`}
                    onClick={() => onBedroomFilterChange(bedroomFilter === b ? null : b)}>
                    {b} BR
                  </button>
                ))}
              </div>
            </div>

            {/* Furnishing */}
            <div className="filter-group">
              <label>Furnishing</label>
              <div className="pill-row">
                {FURNISH_OPTS.map(f => (
                  <button key={f}
                    className={`filter-pill${furnishFilter === f ? ' active' : ''}`}
                    onClick={() => onFurnishFilterChange(furnishFilter === f ? null : f)}>
                    {f}
                  </button>
                ))}
              </div>
            </div>

            {/* Property type */}
            <div className="filter-group">
              <label>Property Type</label>
              <div className="pill-row wrap">
                {PROP_TYPES.map(t => (
                  <button key={t}
                    className={`filter-pill${propTypeFilter.includes(t) ? ' active' : ''}`}
                    onClick={() => onPropTypeFilterChange(propTypeFilter.includes(t) ? propTypeFilter.filter(p => p !== t) : [...propTypeFilter, t])}>
                    {t}
                  </button>
                ))}
              </div>
            </div>

            {activeFilterCount > 0 && (
              <button className="clear-filters" onClick={() => {
                onBedroomFilterChange(null);
                onFurnishFilterChange(null);
                onPropTypeFilterChange([]);
                onPriceFilterChange(0, 9999);
              }}>
                Clear all filters
              </button>
            )}
          </div>
        )}
      </div>

      {/* ── List ── */}
      <div className="rentals-list">
        {filteredRentals.length === 0 && !loading && activeFilterCount > 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">⊟</div>
            <p className="empty-state-title">No rentals match your filters</p>
            <p className="empty-state-sub">Try adjusting or clearing your filters to see more results.</p>
            <button className="empty-state-clear" onClick={() => {
              onBedroomFilterChange(null);
              onFurnishFilterChange(null);
              onPropTypeFilterChange([]);
              onPriceFilterChange(0, 9999);
            }}>
              Clear filters
            </button>
          </div>
        ) : (
          filteredRentals.map(rental => (
            <div
              key={rental.id}
              className={`rental-card${selectedRental?.id === rental.id ? ' selected' : ''}`}
              onClick={() => onSelectRental(rental)}
            >
              <div className="rental-price">
                <span className="price">${rental.monthly_rent.toLocaleString()}</span>
                <span className="period">/mo</span>
              </div>
              <div className="rental-details">
                <div className="rental-header">
                  <span className="bedrooms">🛏 {rental.bedrooms} BR{rental.baths ? ` · 🚿${rental.baths}` : ''}</span>
                  <span className="city">{rental.city}</span>
                </div>
                <p className="address">{rental.address}</p>
                <div className="amenities">
                  {rental.property_type && <span className="amenity">{rental.property_type}</span>}
                  {rental.furnishing && <span className="amenity">{rental.furnishing}</span>}
                  {rental.pets_allowed_status === 'Yes' && <span className="amenity">🐾 Pets</span>}
                  {rental.utilities_included?.length ? <span className="amenity">⚡ Utilities</span> : null}
                </div>
                <div className="rental-footer">
                  <span className="residents">{rental.residents_type}</span>
                  <div className="rental-footer-right">
                    {rental.reports > 0 && <span className="reports">⚠ {rental.reports} reports</span>}
                    {onViewDetails && (
                      <button
                        className="view-details-btn"
                        onClick={e => { e.stopPropagation(); onViewDetails(rental); }}
                      >
                        View details →
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))
        )}

        {hasMore && (
          <button className="load-more-btn" onClick={onLoadMore} disabled={loading}>
            {loading ? 'Loading…' : 'Load More'}
          </button>
        )}
        {loading && filteredRentals.length === 0 && (
          <div className="loading-state">Loading rentals…</div>
        )}
      </div>
    </div>
  );
};
